import React, { useMemo, useState } from 'react';
import styles from './IndustryInsights.module.css';

const INSIGHTS_ARTICLES = [
  {
    id: 'hidden-carbon-cost',
    title: 'The Hidden Carbon Cost of Doing Nothing Differently',
    date: '2026-04-22',
    previewImage: '/images/insights/article1/1.png',
    previewImageAlt: 'Airport ramp operations',
    sourceUrl:
      'https://www.linkedin.com/pulse/hidden-carbon-cost-doing-nothing-differently-neural-lab-t26lc',
    excerpt:
      "Sustainability in aviation is often discussed through the lens of hardware. But there is a massive, often invisible contributor to the industry's carbon footprint that cannot be solved by changing a battery...",
    sections: [
      {
        type: 'intro',
        text: 'Sustainability in aviation is often discussed through the lens of hardware: transitioning to electric ground support equipment or adopting sustainable aviation fuels. However, there is a massive, often invisible contributor to the industry\'s carbon footprint that cannot be solved by changing a battery. It is the hidden ledger of operational waste driven by inefficient orchestration.\n\nWhen ground handling plans remain fixed while the reality of the day-of-ops shifts, the impact extends beyond lost productivity to a growing carbon footprint. Every unladen vehicle movement and every idle engine waiting for a delayed flight contributes to unnecessary emissions across the airside ecosystem. This Earth Day, we must look beyond equipment alone and focus on operational precision as smarter coordination is key to reducing environmental impact and building truly sustainable airport operations.'
      },
      {
        type: 'image',
        imagePath: '/images/insights/article1/1.png',
        imageAlt: 'Airport ramp operations with ground vehicles',
        imageCaption: ''
      },
      {
        type: 'heading',
        text: 'The Carbon Footprint of "Unladen" Trips'
      },
      {
        type: 'body',
        text: 'The most expensive miles driven on an airport ramp are the ones that serve no purpose. And right now, we are generating them by design. When ground handling plans are built around flights rather than tasks, every delay becomes a cascade. Drivers wait, engines idle, and supervisors spend their cognitive capacity on real-time coordination that is avoidable.\n\nThe environmental and economic toll of this coordination failure is staggering, and it is almost entirely invisible on a sustainability balance sheet. Globally, the cost of mishandled bags alone is projected at $3.5 billion annually, a significant portion of which can be traced back to flight-based allocation logic that cannot keep pace with modern volatility.'
      },
      {
        type: 'image',
        imagePath: '/images/insights/article1/2.png',
        imageAlt: 'Data visualisation of carbon costs from operational inefficiency',
        imageCaption: ''
      },
      {
        type: 'heading',
        text: 'True Sustainability: Doing More With What You Already Have'
      },
      {
        type: 'body',
        text: 'Many believe that meeting rising flight volumes requires significant investment in new assets and headcount. Our work with SATS at Singapore Changi Airport suggests otherwise: meaningful recoverable capacity already exists within current fleets.\n\nDeploying OpsAssist at Changi Airport to manage baggage handling orchestration - matching available drivers to the nearest eligible task in real time - produced measurable results from Day 1:'
      },
      {
        type: 'bullets',
        items: [
          '20% increase in operational capacity, achieved by removing idle time and unnecessary travel',
          '50% reduction in driver overtime, creating a safer working environment for frontline staff'
        ]
      },
      {
        type: 'body',
        text: 'The capacity gain at SATS was not achieved by adding assets. It was achieved by removing the coordination friction that was preventing existing vehicles from being useful. Optimising the movement of every vehicle on the ramp not only saves time but also directly reduces fuel burn and cuts equipment wear, closing the gap between environmental ambition and daily operational reality.\n\nRadio coordination can manage but the cost of inaction compounds. The question is not whether to invest, but whether to invest in assets or in the orchestration layer that makes existing assets more productive.'
      },
      {
        type: 'image',
        imagePath: '/images/insights/article1/3.png',
        imageAlt: 'OpsAssist platform showing real-time task orchestration at Changi Airport',
        imageCaption: ''
      },
      {
        type: 'heading',
        text: 'Solving the Right Problem'
      },
      {
        type: 'body',
        text: 'Many AI adoption initiatives fail quietly because they focus on vanity metrics rather than operational effectiveness. At Neural Lab, our methodology focuses on the Right Problem.\n\nBefore writing a single line of code, our team spent six months analysing data at Changi Airport alongside SATS, one of the world\'s largest ground handlers. This was not a technology-first exercise. It was a deliberate search for the core: measurable bottlenecks with clear economic and environmental value. The analysis pointed us toward towing operations, where the value concentration was highest and where orchestration inefficiency was most acute. That discipline of diagnosis before development is what made the outcome credible.'
      },
      {
        type: 'heading',
        text: 'Precision Is the Most Effective Environmental Tool We Have'
      },
      {
        type: 'body',
        text: 'Strengthening decarbonisation is a shared goal across the aviation industry. While significant investment has been made in the hardware of sustainability, the "hidden environmental cost" of miscoordination is often overlooked.\n\nSustainability is not a separate initiative; it is a byproduct of operational excellence. Every unnecessary mile eliminated represents fuel not burned. Every asset operating at full utilisation is one less piece of equipment required.\n\nThe greener ramp is already within reach. It is waiting inside the capacity that improved orchestration can unlock. This Earth Day, let us commit to the precision that enables truly sustainable aviation.'
      },
      {
        type: 'image',
        imagePath: '/images/insights/article1/4.png',
        imageAlt: 'Sustainable airport operations of the future',
        imageCaption: ''
      }
    ]
  }
];

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric'
});

const getExcerpt = article =>
  article.excerpt ||
  `${(article.sections.find(section => section.text)?.text || '').slice(0, 160).trim()}...`;

function IndustryInsights() {
  const [selectedArticle, setSelectedArticle] = useState(null);

  const sortedArticles = useMemo(
    () =>
      [...INSIGHTS_ARTICLES].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      ),
    []
  );

  if (!selectedArticle) {
    return (
      <div>
        {sortedArticles.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No insights published yet.</h3>
            <p>Check back soon for deep dives and thought leadership articles.</p>
          </div>
        ) : (
          <div className={styles.insightsGrid}>
            {sortedArticles.map(article => (
              <article
                key={article.id}
                className={styles.insightTile}
                onClick={() => setSelectedArticle(article)}
                role="button"
                tabIndex={0}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedArticle(article);
                  }
                }}
              >
                {article.previewImage && (
                  <img
                    src={process.env.PUBLIC_URL + article.previewImage}
                    alt={article.previewImageAlt || article.title}
                    className={styles.tileImage}
                  />
                )}
                <div className={styles.tileBody}>
                  <p className={styles.tileDate}>
                    {dateFormatter.format(new Date(article.date))}
                  </p>
                  <h4 className={styles.tileTitle}>{article.title}</h4>
                  <p className={styles.tileExcerpt}>{getExcerpt(article)}</p>
                  <span className={styles.readMore}>Read more {'\u2192'}</span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <article className={styles.articleWrapper}>
      <button
        type="button"
        className={styles.backButton}
        onClick={() => setSelectedArticle(null)}
      >
        {'\u2190'} Back to Insights
      </button>

      <h2 className={styles.articleTitle}>{selectedArticle.title}</h2>
      <p className={styles.articleMeta}>
        <span>{dateFormatter.format(new Date(selectedArticle.date))}</span>
        {' \u2022 '}
        <a
          href={selectedArticle.sourceUrl}
          className={styles.articleSourceLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          View on LinkedIn 
        </a>
      </p>

      {selectedArticle.sections.map((section, index) => {
        if (section.type === 'heading') {
          return (
            <section key={`${section.type}-${index}`} className={styles.articleSection}>
              <h3 className={styles.articleHeading}>{section.text}</h3>
            </section>
          );
        }

        if (section.type === 'bullets') {
          return (
            <section key={`${section.type}-${index}`} className={styles.articleSection}>
              <ul className={styles.articleBullets}>
                {section.items.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          );
        }

        if (section.type === 'image') {
          return (
            <section key={`${section.type}-${index}`} className={styles.articleSection}>
              <img
                src={process.env.PUBLIC_URL + section.imagePath}
                alt={section.imageAlt}
                className={styles.articleImage}
              />
              {section.imageCaption ? (
                <p className={styles.articleCaption}>{section.imageCaption}</p>
              ) : null}
            </section>
          );
        }

        return (
          <section key={`${section.type}-${index}`} className={styles.articleSection}>
            <p className={styles.articleBody}>{section.text}</p>
          </section>
        );
      })}
    </article>
  );
}

export default IndustryInsights;
